import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Skeleton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import apiApplicationService from "../../../service/apiApplicationService";
import { ApiApplication } from "./types";
import TokenRevealDialog from "./TokenRevealDialog";
import { useThemeColors } from "../../../hooks/useThemeColors";

type BusyAction = "regenerate" | "revoke" | "reactivate" | "delete";

const ApiApplicationsList: React.FC = () => {
  const navigate = useNavigate();
  const headingColor = useThemeColors().primary[100];

  const [applications, setApplications] = useState<ApiApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<{ id: number; action: BusyAction } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuApp, setMenuApp] = useState<ApiApplication | null>(null);
  const [revealToken, setRevealToken] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ApiApplication | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setApplications(await apiApplicationService.list());
    } catch {
      toast.error("Failed to load API applications", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openMenu = (e: React.MouseEvent<HTMLElement>, app: ApiApplication) => {
    setMenuAnchor(e.currentTarget);
    setMenuApp(app);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuApp(null);
  };

  const runAction = async (
    app: ApiApplication,
    action: BusyAction,
    fn: () => Promise<void>,
    failMessage: string,
  ) => {
    setBusy({ id: app.id, action });
    try {
      await fn();
      await load();
    } catch {
      toast.error(failMessage, { position: "top-right" });
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = (app: ApiApplication) => {
    closeMenu();
    runAction(
      app,
      "regenerate",
      async () => {
        const { token } = await apiApplicationService.regenerateToken(app.id);
        setRevealToken(token);
      },
      "Failed to regenerate token",
    );
  };

  const handleRevokeToggle = (app: ApiApplication) => {
    closeMenu();
    const action: BusyAction = app.is_active ? "revoke" : "reactivate";
    runAction(
      app,
      action,
      async () => {
        if (app.is_active) {
          await apiApplicationService.revoke(app.id);
          toast.success(`"${app.name}" revoked`, { position: "top-right" });
        } else {
          await apiApplicationService.reactivate(app.id);
          toast.success(`"${app.name}" reactivated`, { position: "top-right" });
        }
      },
      "Failed to update application status",
    );
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    const app = confirmDelete;
    runAction(
      app,
      "delete",
      async () => {
        await apiApplicationService.remove(app.id);
        toast.success(`"${app.name}" deleted`, { position: "top-right" });
        setConfirmDelete(null);
      },
      "Failed to delete application",
    );
  };

  const isDeleting = busy?.action === "delete" && busy.id === confirmDelete?.id;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="body2" color="text.secondary">
          Service-account tokens for external integrations.
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/configurations/api-applications/add")}
        >
          New Application
        </Button>
      </Stack>

      {loading ? (
        <Stack spacing={2}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={88} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : applications.length === 0 ? (
        <Paper
          sx={{
            boxShadow: 0,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 3,
            textAlign: "center",
            py: 8,
          }}
        >
          <KeyIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">No API applications yet.</Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {applications.map((app) => {
            const isBusy = busy?.id === app.id;
            return (
              <Paper
                key={app.id}
                onClick={() => !isBusy && navigate(`/configurations/api-applications/edit/${app.id}`)}
                sx={{
                  boxShadow: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  p: 2.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: isBusy ? "default" : "pointer",
                  opacity: isBusy ? 0.7 : 1,
                  "&:hover": { backgroundColor: isBusy ? undefined : "action.hover" },
                }}
              >
                <Box minWidth={0}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontWeight={700} noWrap sx={{ color: headingColor }}>
                      {app.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={app.is_active ? "Active" : "Revoked"}
                      color={app.is_active ? "success" : "default"}
                      variant={app.is_active ? "filled" : "outlined"}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                    {app.token_prefix}••••••••  ·  {app.grant_count} model
                    {app.grant_count === 1 ? "" : "s"} granted  ·  hosts:{" "}
                    {app.allowed_hosts.join(", ")}
                  </Typography>
                </Box>
                {isBusy ? (
                  <CircularProgress size={20} sx={{ mx: 1.25 }} />
                ) : (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      openMenu(e, app);
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            const app = menuApp;
            closeMenu();
            if (app) navigate(`/configurations/api-applications/edit/${app.id}`);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem onClick={() => menuApp && handleRegenerate(menuApp)}>Regenerate Token</MenuItem>
        <MenuItem onClick={() => menuApp && handleRevokeToggle(menuApp)}>
          {menuApp?.is_active ? "Revoke" : "Reactivate"}
        </MenuItem>
        <MenuItem
          sx={{ color: "error.main" }}
          onClick={() => {
            const app = menuApp;
            closeMenu();
            if (app) setConfirmDelete(app);
          }}
        >
          Delete
        </MenuItem>
      </Menu>

      <TokenRevealDialog token={revealToken} onClose={() => setRevealToken(null)} />

      <Dialog open={Boolean(confirmDelete)} onClose={() => !isDeleting && setConfirmDelete(null)}>
        <DialogTitle sx={{ color: headingColor }}>Delete "{confirmDelete?.name}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This permanently removes the application and its token. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiApplicationsList;
